import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { BarInterval } from '../../api/marketData'
import type {
  UserIndicatorConfig,
  UserEmaIndicator,
  UserOrbIndicator,
  UserSmaIndicator,
  UserVwapIndicator,
  UserPremarketHighLowIndicator,
  UserPrevDayHighLowIndicator,
  UserVolumeIndicator,
} from '../../lib/indicators/types'
import { getDefaultConfig } from '../../lib/indicators/defaultPreset'
import type { UserIndicator } from '../../lib/indicators/types'


// ── Helpers ──────────────────────────────────────────────────────────────────

function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
}

// ── Props ────────────────────────────────────────────────────────────────────

export interface IndicatorSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  interval: BarInterval
  currentConfig: UserIndicatorConfig
  onSave: (config: UserIndicatorConfig) => void
}

// ── Color palette ────────────────────────────────────────────────────────────

const COLOR_PALETTE = [
  '#10b981', '#34d399', '#6ee7b7', '#a7f3d0', // greens
  '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', // blues
  '#ef4444', '#f87171', '#fca5a5', '#fecaca', // reds
  '#eab308', '#facc15', '#fde047', '#fef08a', // yellows
  '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', // purples
  '#06b6d4', '#22d3ee', '#67e8f9', '#a5f3fc', // cyans
  '#3f3f46', '#71717a', '#a1a1aa', '#d4d4d8', // grays
] as const

// ── Color Picker Component ───────────────────────────────────────────────────

function ColorPicker({ value, onChange, ariaLabel }: {
  value: string
  onChange: (color: string) => void
  ariaLabel: string
}) {
  const [open, setOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div className="relative shrink-0">
      {/* Trigger swatch */}
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(!open) }}
        className="w-7 h-7 rounded cursor-pointer border border-zinc-700/50 hover:border-zinc-500 transition-colors"
        style={{ backgroundColor: value }}
      />
      {/* Popover grid */}
      {open && createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[100] bg-zinc-800 border border-zinc-700/60 rounded-lg shadow-xl p-2.5"
          style={{
            top: (triggerRef.current?.getBoundingClientRect().bottom ?? 0) + 4,
            left: triggerRef.current?.getBoundingClientRect().left ?? 0,
          }}
        >
          <div className="grid grid-cols-6 gap-1.5">
            {COLOR_PALETTE.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => { onChange(color); setOpen(false) }}
                className={`w-6 h-6 rounded-sm border transition-transform hover:scale-125 ${
                  value === color ? 'border-white ring-1 ring-white/30' : 'border-zinc-600/40'
                }`}
                style={{ backgroundColor: color }}
                aria-label={color}
              />
            ))}
          </div>
          {/* Custom color row */}
          <div className="flex items-center gap-2 mt-2.5 pt-2 border-t border-zinc-700/40">
            <label className="text-[9px] uppercase tracking-wide text-zinc-500 shrink-0">Custom</label>
            <input
              type="color"
              value={value}
              onChange={(e) => { onChange(e.target.value); setOpen(false) }}
              className="w-6 h-6 rounded cursor-pointer border-0 p-0 bg-transparent"
              aria-label="Custom color"
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}


function EmaSmaEditor({
  indicators,
  onChange,
  kind,
  allIndicators,
}: {
  indicators: (UserEmaIndicator | UserSmaIndicator)[]
  onChange: (indicators: (UserEmaIndicator | UserSmaIndicator)[]) => void
  kind: 'ema' | 'sma'
  allIndicators: UserIndicator[]
}) {
  const INDICATOR_PALETTE = Array.from(COLOR_PALETTE)

  function update(idx: number, patch: Partial<UserEmaIndicator | UserSmaIndicator>) {
    const next = indicators.map((ind, i) => (i === idx ? { ...ind, ...patch } : ind))
    onChange(next)
  }

  function remove(idx: number) {
    onChange(indicators.filter((_, i) => i !== idx))
  }

  function add() {
    // Pick a color not already used by any MA/VWAP indicator
    const usedColors = new Set<string>()
    for (const ind of allIndicators) {
      if (ind.type === 'ema' || ind.type === 'sma' || ind.type === 'vwap') {
        usedColors.add(ind.color)
      }
    }
    let color = INDICATOR_PALETTE[0]
    for (const c of INDICATOR_PALETTE) {
      if (!usedColors.has(c)) { color = c; break }
    }
    const base: UserEmaIndicator | UserSmaIndicator =
      kind === 'ema'
        ? {
            id: uid(),
            type: 'ema',
            enabled: true,
            period: 20,
            color,
            width: 1,
            style: 'solid',
          }
        : {
            id: uid(),
            type: 'sma',
            enabled: true,
            period: 20,
            color,
            width: 1,
            style: 'solid',
          }
    onChange([...indicators, base])
  }

  return (
    <div className="space-y-2">
      {indicators.length === 0 && (
        <p className="text-xs text-zinc-500 italic">No {kind.toUpperCase()} lines added.</p>
      )}
      {indicators.map((ind, idx) => (
        <div
          key={ind.id}
          className="flex items-center gap-2 bg-zinc-800/60 rounded-lg px-3 py-2 border border-zinc-700/50"
        >
          {/* color swatch */}
          <ColorPicker
            value={ind.color}
            onChange={(color) => update(idx, { color })}
            ariaLabel={`${kind} color`}
          />
          {/* period */}
          <div className="flex items-center gap-1">
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 shrink-0">
              {kind.toUpperCase()}
            </label>
            <input
              type="number"
              min={1}
              max={10000}
              value={ind.period}
              onChange={(e) => update(idx, { period: parseInt(e.target.value, 10) || 1 })}
              className="w-16 bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-zinc-200 font-mono focus:outline-none focus:border-zinc-500"
            />
          </div>
          {/* width */}
          <div className="flex items-center gap-1">
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 shrink-0">
              Width
            </label>
            <input
              type="range"
              min={1}
              max={4}
              step={1}
              value={ind.width}
              onChange={(e) => update(idx, { width: parseInt(e.target.value, 10) || 1 })}
              className="w-16 accent-zinc-400"
              aria-label={`${kind} line width`}
            />
            <span className="text-xs text-zinc-400 font-mono w-3 shrink-0">{ind.width}</span>
          </div>
          {/* line style */}
          <div className="flex items-center gap-1">
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 shrink-0">
              Style
            </label>
            <select
              value={ind.style ?? 'solid'}
              onChange={(e) => update(idx, { style: e.target.value as 'solid' | 'dashed' | 'dotted' })}
              className="bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-zinc-200 focus:outline-none focus:border-zinc-500"
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
            </select>
          </div>
          {/* enabled toggle */}
          <label className="flex items-center gap-1 cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={ind.enabled}
              onChange={(e) => update(idx, { enabled: e.target.checked })}
              className="rounded bg-zinc-900 border-zinc-600 accent-zinc-400"
            />
            <span className="text-xs text-zinc-400">On</span>
          </label>
          {/* remove */}
          <button
            type="button"
            onClick={() => remove(idx)}
            className="ml-auto text-zinc-500 hover:text-red-400 transition-colors shrink-0"
            aria-label={`Remove ${kind} ${ind.period}`}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M3 3l8 8M11 3l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="text-xs text-zinc-400 hover:text-zinc-200 transition-colors flex items-center gap-1"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        Add {kind.toUpperCase()}
      </button>
    </div>
  )
}

function VwapEditor({
  indicator,
  onChange,
}: {
  indicator: UserVwapIndicator
  onChange: (ind: UserVwapIndicator) => void
}) {
  return (
    <div className="space-y-3 bg-zinc-800/60 rounded-lg px-3 py-2 border border-zinc-700/50">
      <div className="flex items-center gap-3">
        <ColorPicker
          value={indicator.color}
          onChange={(color) => onChange({ ...indicator, color })}
          ariaLabel="VWAP color"
        />
        {/* source */}
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wide text-zinc-500">Source</label>
          <select
            value={indicator.source}
            onChange={(e) =>
              onChange({ ...indicator, source: e.target.value as 'hlc3' | 'close' })
            }
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-0.5 text-xs text-zinc-200 focus:outline-none focus:border-zinc-500"
          >
            <option value="hlc3">HL(C)3</option>
            <option value="close">Close</option>
          </select>
        </div>
        {/* width */}
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wide text-zinc-500">Width</label>
          <input
            type="range"
            min={1}
            max={4}
            step={1}
            value={indicator.width}
            onChange={(e) => onChange({ ...indicator, width: parseInt(e.target.value, 10) || 1 })}
            className="w-16 accent-zinc-400"
            aria-label="VWAP line width"
          />
          <span className="text-xs text-zinc-400 font-mono w-3 shrink-0">{indicator.width}</span>
        </div>
        {/* line style */}
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wide text-zinc-500">Style</label>
          <select
            value={indicator.style ?? 'solid'}
            onChange={(e) => onChange({ ...indicator, style: e.target.value as 'solid' | 'dashed' | 'dotted' })}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-0.5 text-xs text-zinc-200 focus:outline-none focus:border-zinc-500"
          >
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
          </select>
        </div>
      </div>
      {/* enabled toggle */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={indicator.enabled}
          onChange={(e) => onChange({ ...indicator, enabled: e.target.checked })}
          className="rounded bg-zinc-900 border-zinc-600 accent-zinc-400"
        />
        <span className="text-xs text-zinc-400">Enabled</span>
      </label>
    </div>
  )
}

// ── Indicator type display names ─────────────────────────────────────────────

const INDICATOR_LABELS: Record<string, string> = {
  ema: 'Exponential Moving Average',
  sma: 'Simple Moving Average',
  vwap: 'VWAP',
  premarketHighLow: 'Premarket High / Low',
  prevDayHighLow: 'Previous Day High / Low',
  orb: 'Opening Range Breakout (ORB)',
  volume: 'Volume',
}

// ── Main modal ───────────────────────────────────────────────────────────────

export function IndicatorSettingsModal({
  isOpen,
  onClose,
  interval,
  currentConfig,
  onSave,
}: IndicatorSettingsModalProps) {
  // Local state for the config being edited
  const [config, setConfig] = useState<UserIndicatorConfig>(() =>
    structuredClone(currentConfig),
  )

  // Reset local state when the modal opens or currentConfig changes
  useEffect(() => {
    if (isOpen) {
      setConfig(structuredClone(currentConfig))
    }
  }, [isOpen, currentConfig])

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  // ── Derived groups ───────────────────────────────────────────────────────

  const emaIndicators = useMemo(
    () => config.indicators.filter((i) => i.type === 'ema') as UserEmaIndicator[],
    [config.indicators],
  )
  const smaIndicators = useMemo(
    () => config.indicators.filter((i) => i.type === 'sma') as UserSmaIndicator[],
    [config.indicators],
  )
  const vwapIndicator = useMemo(
    () =>
      (config.indicators.find((i) => i.type === 'vwap') ?? null) as UserVwapIndicator | null,
    [config.indicators],
  )
  const premarketIndicator = useMemo(
    () =>
      (config.indicators.find((i) => i.type === 'premarketHighLow') ?? null) as UserPremarketHighLowIndicator | null,
    [config.indicators],
  )
  const prevDayIndicator = useMemo(
    () =>
      (config.indicators.find((i) => i.type === 'prevDayHighLow') ?? null) as UserPrevDayHighLowIndicator | null,
    [config.indicators],
  )
  const orbIndicator = useMemo(
    () =>
      (config.indicators.find((i) => i.type === 'orb') ?? null) as UserOrbIndicator | null,
    [config.indicators],
  )
  const volumeIndicator = useMemo(
    () =>
      (config.indicators.find((i) => i.type === 'volume') ?? null) as UserVolumeIndicator | null,
    [config.indicators],
  )

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleReset = useCallback(() => {
    setConfig(structuredClone(getDefaultConfig(interval)))
  }, [interval])

  const handleSave = useCallback(() => {
    const saved: UserIndicatorConfig = {
      version: config.version,
      indicators: config.indicators,
    }
    onSave(saved)
    onClose()
  }, [config.version, config.indicators, onSave, onClose])

  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  // ── Render ───────────────────────────────────────────────────────────────

  if (!isOpen) return null

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={handleCancel}
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* panel */}
      <div
        className="relative z-10 w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden rounded-xl border border-zinc-700/60 shadow-2xl bg-zinc-900"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-700/50">
          <h2 className="text-sm font-semibold text-zinc-100 tracking-wide">
            Chart Indicators
          </h2>
          <button
            type="button"
            onClick={handleCancel}
            className="text-zinc-500 hover:text-zinc-200 transition-colors"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path
                d="M5 5l8 8M13 5l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* body */}
        <div className="overflow-y-auto px-5 py-4 space-y-5" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          {/* EMA */}
          <div>
            <h3 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">
              {INDICATOR_LABELS.ema}
            </h3>
            <EmaSmaEditor
              indicators={emaIndicators}
              onChange={(indicators) =>
                setConfig((prev) => ({
                  ...prev,
                  indicators: [
                    ...indicators,
                    ...prev.indicators.filter((i) => i.type !== 'ema'),
                  ],
                }))
              }
              kind="ema"
              allIndicators={config.indicators}
            />
          </div>

          {/* SMA */}
          <div>
            <h3 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">
              {INDICATOR_LABELS.sma}
            </h3>
            <EmaSmaEditor
              indicators={smaIndicators}
              onChange={(indicators) =>
                setConfig((prev) => ({
                  ...prev,
                  indicators: [
                    ...indicators,
                    ...prev.indicators.filter((i) => i.type !== 'sma'),
                  ],
                }))
              }
              kind="sma"
              allIndicators={config.indicators}
            />
          </div>

          {/* VWAP */}
          <div>
            <h3 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">
              {INDICATOR_LABELS.vwap}
            </h3>
            {vwapIndicator ? (
              <VwapEditor
                indicator={vwapIndicator}
                onChange={(ind) =>
                  setConfig((prev) => ({
                    ...prev,
                    indicators: prev.indicators.map((i) => (i.type === 'vwap' ? ind : i)),
                  }))
                }
              />
            ) : (
              <p className="text-xs text-zinc-500 italic">Not available for this interval.</p>
            )}
          </div>

          {/* Premarket High / Low */}
          <div>
            <h3 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">
              {INDICATOR_LABELS.premarketHighLow}
            </h3>
            {premarketIndicator ? (
              <div className="space-y-3 bg-zinc-800/60 rounded-lg px-3 py-2 border border-zinc-700/50">
                {/* color pickers */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500">High</span>
                    <ColorPicker
                      value={premarketIndicator.highColor}
                      onChange={(color) =>
                        setConfig((prev) => ({
                          ...prev,
                          indicators: prev.indicators.map((i) =>
                            i.type === 'premarketHighLow' ? { ...i, highColor: color } : i,
                          ),
                        }))
                      }
                      ariaLabel="Premarket High color"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500">Low</span>
                    <ColorPicker
                      value={premarketIndicator.lowColor}
                      onChange={(color) =>
                        setConfig((prev) => ({
                          ...prev,
                          indicators: prev.indicators.map((i) =>
                            i.type === 'premarketHighLow' ? { ...i, lowColor: color } : i,
                          ),
                        }))
                      }
                      ariaLabel="Premarket Low color"
                    />
                  </div>
                </div>
                {/* width */}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-wide text-zinc-500">Width</label>
                  <input
                    type="range"
                    min={1}
                    max={4}
                    step={1}
                    value={premarketIndicator.width ?? 1}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'premarketHighLow' ? { ...i, width: parseInt(e.target.value, 10) || 1 } : i,
                        ),
                      }))
                    }
                    className="w-16 accent-zinc-400"
                    aria-label="Premarket line width"
                  />
                  <span className="text-xs text-zinc-400 font-mono w-3 shrink-0">{premarketIndicator.width ?? 1}</span>
                </div>
                {/* line style */}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-wide text-zinc-500">Style</label>
                  <select
                    value={premarketIndicator.style ?? 'dashed'}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'premarketHighLow' ? { ...i, style: e.target.value as 'solid' | 'dashed' | 'dotted' } : i,
                        ),
                      }))
                    }
                    className="bg-zinc-900 border border-zinc-700 rounded px-2 py-0.5 text-xs text-zinc-200 focus:outline-none focus:border-zinc-500"
                  >
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                      <option value="dotted">Dotted</option>
                  </select>
                </div>
                {/* enabled toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={premarketIndicator.enabled}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'premarketHighLow' ? { ...i, enabled: e.target.checked } : i,
                        ),
                      }))
                    }
                    className="rounded bg-zinc-900 border-zinc-600 accent-zinc-400"
                  />
                  <span className="text-xs text-zinc-400">Show premarket high / low</span>
                </label>
              </div>
            ) : (
              <p className="text-xs text-zinc-500 italic">Not available for this interval.</p>
            )}
          </div>
          {/* Previous Day High / Low */}
          <div>
            <h3 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">
              {INDICATOR_LABELS.prevDayHighLow}
            </h3>
            {prevDayIndicator ? (
              <div className="space-y-3 bg-zinc-800/60 rounded-lg px-3 py-2 border border-zinc-700/50">
                {/* color pickers */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500">High</span>
                    <ColorPicker
                      value={prevDayIndicator.highColor}
                      onChange={(color) =>
                        setConfig((prev) => ({
                          ...prev,
                          indicators: prev.indicators.map((i) =>
                            i.type === 'prevDayHighLow' ? { ...i, highColor: color } : i,
                          ),
                        }))
                      }
                      ariaLabel="Previous Day High color"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500">Low</span>
                    <ColorPicker
                      value={prevDayIndicator.lowColor}
                      onChange={(color) =>
                        setConfig((prev) => ({
                          ...prev,
                          indicators: prev.indicators.map((i) =>
                            i.type === 'prevDayHighLow' ? { ...i, lowColor: color } : i,
                          ),
                        }))
                      }
                      ariaLabel="Previous Day Low color"
                    />
                  </div>
                </div>
                {/* width */}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-wide text-zinc-500">Width</label>
                  <input
                    type="range"
                    min={1}
                    max={4}
                    step={1}
                    value={prevDayIndicator.width ?? 1}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'prevDayHighLow' ? { ...i, width: parseInt(e.target.value, 10) || 1 } : i,
                        ),
                      }))
                    }
                    className="w-16 accent-zinc-400"
                    aria-label="Previous day line width"
                  />
                  <span className="text-xs text-zinc-400 font-mono w-3 shrink-0">{prevDayIndicator.width ?? 1}</span>
                </div>
                {/* line style */}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-wide text-zinc-500">Style</label>
                  <select
                    value={prevDayIndicator.style ?? 'dashed'}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'prevDayHighLow' ? { ...i, style: e.target.value as 'solid' | 'dashed' | 'dotted' } : i,
                        ),
                      }))
                    }
                    className="bg-zinc-900 border border-zinc-700 rounded px-2 py-0.5 text-xs text-zinc-200 focus:outline-none focus:border-zinc-500"
                  >
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                      <option value="dotted">Dotted</option>
                  </select>
                </div>
                {/* enabled toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={prevDayIndicator.enabled}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'prevDayHighLow' ? { ...i, enabled: e.target.checked } : i,
                        ),
                      }))
                    }
                    className="rounded bg-zinc-900 border-zinc-600 accent-zinc-400"
                  />
                  <span className="text-xs text-zinc-400">Show previous day high / low</span>
                </label>
              </div>
            ) : (
              <p className="text-xs text-zinc-500 italic">Not available for this interval.</p>
            )}
          </div>

          {/* Opening Range Breakout (ORB) */}
          <div>
            <h3 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">
              {INDICATOR_LABELS.orb}
            </h3>
            {orbIndicator ? (
              <div className="space-y-3 bg-zinc-800/60 rounded-lg px-3 py-2 border border-zinc-700/50">
                {/* color pickers */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500">High</span>
                    <ColorPicker
                      value={orbIndicator.highColor}
                      onChange={(color) =>
                        setConfig((prev) => ({
                          ...prev,
                          indicators: prev.indicators.map((i) =>
                            i.type === 'orb' ? { ...i, highColor: color } : i,
                          ),
                        }))
                      }
                      ariaLabel="ORB High color"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500">Low</span>
                    <ColorPicker
                      value={orbIndicator.lowColor}
                      onChange={(color) =>
                        setConfig((prev) => ({
                          ...prev,
                          indicators: prev.indicators.map((i) =>
                            i.type === 'orb' ? { ...i, lowColor: color } : i,
                          ),
                        }))
                      }
                      ariaLabel="ORB Low color"
                    />
                  </div>
                </div>
                {/* width */}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-wide text-zinc-500">Width</label>
                  <input
                    type="range"
                    min={1}
                    max={4}
                    step={1}
                    value={orbIndicator.width ?? 1}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'orb' ? { ...i, width: parseInt(e.target.value, 10) || 1 } : i,
                        ),
                      }))
                    }
                    className="w-16 accent-zinc-400"
                    aria-label="ORB line width"
                  />
                  <span className="text-xs text-zinc-400 font-mono w-3 shrink-0">{orbIndicator.width ?? 1}</span>
                </div>
                {/* line style */}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-wide text-zinc-500">Style</label>
                  <select
                    value={orbIndicator.style ?? 'dashed'}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'orb' ? { ...i, style: e.target.value as 'solid' | 'dashed' | 'dotted' } : i,
                        ),
                      }))
                    }
                    className="bg-zinc-900 border border-zinc-700 rounded px-2 py-0.5 text-xs text-zinc-200 focus:outline-none focus:border-zinc-500"
                  >
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                      <option value="dotted">Dotted</option>
                  </select>
                </div>
                {/* enabled toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={orbIndicator.enabled}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'orb' ? { ...i, enabled: e.target.checked } : i,
                        ),
                      }))
                    }
                    className="rounded bg-zinc-900 border-zinc-600 accent-zinc-400"
                  />
                  <span className="text-xs text-zinc-400">Show ORB high / low</span>
                </label>
                {/* shaded area toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={orbIndicator.shaded ?? true}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'orb' ? { ...i, shaded: e.target.checked } : i,
                        ),
                      }))
                    }
                    className="rounded bg-zinc-900 border-zinc-600 accent-zinc-400"
                  />
                  <span className="text-xs text-zinc-400">Shaded area</span>
                </label>
              </div>
            ) : (
              <p className="text-xs text-zinc-500 italic">Not available for this interval.</p>
            )}
          </div>

          {/* Volume */}
          <div>
            <h3 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">
              {INDICATOR_LABELS.volume}
            </h3>
            {volumeIndicator ? (
              <div className="space-y-3 bg-zinc-800/60 rounded-lg px-3 py-2 border border-zinc-700/50">
                {/* color pickers */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500">Up</span>
                    <ColorPicker
                      value={volumeIndicator.upColor}
                      onChange={(color) =>
                        setConfig((prev) => ({
                          ...prev,
                          indicators: prev.indicators.map((i) =>
                            i.type === 'volume' ? { ...i, upColor: color } : i,
                          ),
                        }))
                      }
                      ariaLabel="Volume up-bar color"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500">Down</span>
                    <ColorPicker
                      value={volumeIndicator.downColor}
                      onChange={(color) =>
                        setConfig((prev) => ({
                          ...prev,
                          indicators: prev.indicators.map((i) =>
                            i.type === 'volume' ? { ...i, downColor: color } : i,
                          ),
                        }))
                      }
                      ariaLabel="Volume down-bar color"
                    />
                  </div>
                </div>
                {/* enabled toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={volumeIndicator.enabled}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        indicators: prev.indicators.map((i) =>
                          i.type === 'volume' ? { ...i, enabled: e.target.checked } : i,
                        ),
                      }))
                    }
                    className="rounded bg-zinc-900 border-zinc-600 accent-zinc-400"
                  />
                  <span className="text-xs text-zinc-400">Show volume bars</span>
                </label>
              </div>
            ) : (
              <p className="text-xs text-zinc-500 italic">Not available for this interval.</p>
            )}
          </div>
        </div>

        {/* footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-zinc-700/50">
          <button
            type="button"
            onClick={handleReset}
            className="text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            Reset to Defaults
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-1.5 text-xs text-zinc-300 hover:text-zinc-100 bg-zinc-800 hover:bg-zinc-700 rounded-lg border border-zinc-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-1.5 text-xs text-zinc-900 bg-emerald-500 hover:bg-emerald-400 rounded-lg font-medium transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
